import { useInvalidateOrders } from '@/entities/order';
import { useInvalidatePayments } from '@/entities/payment';
import type { OrderRefundResult } from '@/shared/lib';

interface RefreshAfterRefund {
  /** 환불 결과를 받은 뒤 — 주문·견적·결제 캐시를 모두 갱신한다. */
  readonly afterResult: (orderId: string, result: OrderRefundResult) => Promise<void>;
  /**
   * 환불이 거절됐거나(토스 거절 — 실패한 환불 기록이 생긴다) 처리 중일 때 — 주문은 그대로라 결제 목록·그 주문의 결제 상세만
   * 다시 불러온다. 주문 상태 충돌의 상세 재조회는 대화상자가 정한다.
   */
  readonly afterFailure: () => Promise<void>;
}

/**
 * 환불 처리(취소·환불, 공급 불가 취소) 뒤 캐시 갱신 — 환불이 끝났으면(COMPLETED) 응답 상세를 캐시에 넣고, 결과를 모르면
 * (UNKNOWN) 상세를 다시 불러온다. 어느 쪽이든 주문 목록, 주문 상태를 보여 주는 견적 화면, 결제 목록·그 주문의
 * 결제 상세(잔액·환불 이력)를 다시 불러온다. 실패하면 결제 쪽만 다시 불러온다.
 */
export const useRefreshAfterRefund = (paymentId: string | null): RefreshAfterRefund => {
  const orders = useInvalidateOrders();
  const payments = useInvalidatePayments();
  const refreshPayments = (): Promise<unknown> =>
    Promise.all([payments.list(), paymentId === null ? undefined : payments.detail(paymentId)]);

  return {
    afterResult: async (orderId, result) => {
      if (result.kind === 'COMPLETED') orders.putDetail(result.order);
      await Promise.all([
        result.kind === 'COMPLETED' ? undefined : orders.detail(orderId),
        orders.list(),
        orders.quotes(),
        refreshPayments(),
      ]);
    },
    afterFailure: async () => {
      await refreshPayments();
    },
  };
};
